import type {
  IdvPsc,
  IdvPscStatus,
  IdvSessionStatus,
} from '@pflegenavi/shared/api';

export interface IdvPscAction {
  psc: IdvPsc;
  action: 'open' | 'retry';
}

export interface IdvActions {
  primary: 'start' | 'continue' | null;
  /** The existing session is valid but superseded by the offer to start a new one. */
  collapseExisting: boolean;
  pscActions: IdvPscAction[];
}

const NEGATIVE_STATUSES: IdvSessionStatus[] = [
  'REFUSED',
  'EXPIRED',
  'OUT_OF_DATE',
];
const TEMPORARY_STATUSES: IdvSessionStatus[] = ['PENDING', 'REVIEW'];
const FINISHED_PSC_STATUSES: IdvPscStatus[] = ['VALIDATED', 'ABANDONED'];

const pscActions = (pscs: IdvPsc[]): IdvPscAction[] =>
  pscs.flatMap((psc): IdvPscAction[] => {
    if (psc.status === 'PENDING_VALIDATION' && psc.hosted_url) {
      return [{ psc, action: 'open' }];
    }
    if (psc.status === 'REJECTED') {
      return [{ psc, action: 'retry' }];
    }
    return [];
  });

/** What the KYC dialog offers; a validated session allows a new one only once no PSC is open. */
export const getIdvActions = (
  status: IdvSessionStatus | null | undefined,
  pscs: IdvPsc[]
): IdvActions => {
  if (!status || NEGATIVE_STATUSES.includes(status)) {
    return { primary: 'start', collapseExisting: false, pscActions: [] };
  }
  if (TEMPORARY_STATUSES.includes(status)) {
    return {
      primary: 'continue',
      collapseExisting: false,
      pscActions: pscActions(pscs),
    };
  }
  const allPscsFinished = pscs.every((psc) =>
    FINISHED_PSC_STATUSES.includes(psc.status)
  );
  return allPscsFinished
    ? { primary: 'start', collapseExisting: true, pscActions: [] }
    : { primary: null, collapseExisting: false, pscActions: pscActions(pscs) };
};

const IDV_SESSION_LANGUAGES: Record<string, string> = {
  de: 'de-DE',
  en: 'en-US',
};

/** MangoPay hosted IDV locale for an app language, e.g. `'de'` -> `'de-DE'`; undefined lets MangoPay pick. */
export const idvSessionLanguage = (
  language: string | undefined
): string | undefined =>
  language ? IDV_SESSION_LANGUAGES[language.split('-')[0]] : undefined;
